import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll behaviour on navigation:
 *
 *  - PUSH (new page, e.g. Pay & Book → /checkout) → scroll to top.
 *  - POP  (browser back/forward AND our in-app BackRouteButton, which calls
 *         `navigate(-1)`) → restore the scroll position the user was at on
 *         that route last time.
 *  - REPLACE → no-op (e.g. router strips a hash via `navigate(path, { replace }).
 *
 * Why we manage scroll ourselves (ONENOW-469): the browser's native
 * `scrollRestoration: 'auto'` clamps to `maxScroll` if the destination page
 * is shorter than its previous state. The fleet detail page fetches data
 * async, so on first paint after a POP it's only ~viewport-tall — the
 * browser then "restores" to Y = pageHeight - viewport, which lands the
 * user at the footer. By switching to `manual` and retrying the scroll as
 * the page grows, we land them back at the position they actually left.
 *
 * Third iteration (ONENOW-469 second reopen): even with manual restore +
 * ResizeObserver retries, we were still landing users near the footer.
 * The cause was the restore *itself* — we were clamping `targetY` to the
 * current `maxY` on every retry and scrolling there. When the destination
 * is a spinner-only early-return (the fleet detail page renders a
 * `min-h-[50vh]` spinner while `selectedCar` rehydrates from context),
 * `maxY` is small but nonzero (header + spinner + footer of the SPA
 * shell), and the clamped scroll lands exactly at the footer of that
 * skeleton. Now we hold the user at Y=0 until the document grows enough
 * to honour the full saved position — only then do we scroll there.
 *
 * Fourth iteration (ONENOW-469 third reopen): the third-iteration fix
 * worked the first time tryRestore succeeded, then `stop()` killed the
 * observer + interval. But the fleet detail page often grows in stages:
 * first paint with cached `selectedCar` is tall enough → we restore →
 * we stop → a subsequent re-render shrinks the document briefly
 * (spinner early-return flashing, lazy banner image not yet loaded,
 * async effect swapping content) → the browser clamps scrollY down to
 * the new (small) maxY → when the page finally settles tall again the
 * user is parked near the now-irrelevant maxY of the *intermediate*
 * state. That looks identical to "lands at footer" to QA. Now we keep
 * the interval + ResizeObserver alive through the full 10 s window so
 * any post-restore document shrink gets corrected the next tick; only
 * a genuine user scroll (wheel / touch / scroll-trigger key) or the
 * timeout terminates the watcher.
 */

const KEY = "app_scroll_positions";
// Absolute hard stop for restoration attempts. With a `ResizeObserver` we
// re-scroll whenever the document grows, so under normal conditions the
// final scroll happens within a frame or two of the data resolving — the
// long fallback exists for very slow networks (initial 1.5 s budget was
// too short; the page would still be skeleton when retries gave up and
// the user would land at the bottom of the partial content, ≈ the footer
// — ONENOW-469 reopen).
const RESTORE_TIMEOUT_MS = 10000;
// Spacing for the setInterval backstop (in case ResizeObserver doesn't
// fire — e.g., content height grows via direct style mutation without
// touching the layout in a way the observer notices).
const RESTORE_TICK_MS = 100;
// Window during which we ignore programmatic scroll events that fire as a
// consequence of navigation (the new page is briefly shorter while data
// loads, the browser clamps scrollY downward, and that clamp fires a scroll
// event that we'd otherwise misattribute to the still-current URL).
const NAV_QUIET_MS = 600;

type Positions = Record<string, number>;

const readPositions = (): Positions => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Positions) : {};
  } catch {
    return {};
  }
};

const writePositions = (positions: Positions) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(positions));
  } catch {
    // ignore quota / privacy-mode errors
  }
};

const savePosition = (key: string, y: number) => {
  const positions = readPositions();
  positions[key] = y;
  writePositions(positions);
};

const ScrollToTop: React.FC = () => {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const key = pathname + search;
  const lastNavAtRef = useRef(0);
  const prevKeyRef = useRef(key);
  // Stamp the navigation timestamp synchronously during render — before the
  // commit-time scroll event from page-shrink clamping fires. If we set this
  // in useEffect, the rAF queued by that scroll event can run first and the
  // gate is wide open.
  if (prevKeyRef.current !== key) {
    prevKeyRef.current = key;
    lastNavAtRef.current = Date.now();
  }

  // Disable browser-native scroll restoration once. We restore manually below
  // so async content load can't clamp the user to the bottom of a short page.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Keep the saved scrollY for the *current URL* fresh while the user reads
  // it. We read `window.location` directly inside `snap` rather than closing
  // over React state: `history.pushState` (which react-router's `navigate`
  // calls) updates `window.location` synchronously, but the new React
  // render that propagates the URL to `useLocation` consumers only runs
  // later. The post-navigation scroll event from layout clamping fires
  // *between* those two points — if we used the React-state key here, we'd
  // overwrite the outgoing page's correct value with the clamped scrollY
  // that belongs to the new page. Pointerdown / keydown fire synchronously
  // before navigation, so they always save under the old URL as intended.
  useEffect(() => {
    let frame = 0;
    const snap = () => {
      const currentKey = window.location.pathname + window.location.search;
      savePosition(currentKey, window.scrollY);
    };
    // Scroll-event snap: ignored briefly after a route change, because the
    // browser clamps scrollY when the destination page is initially shorter
    // and that synthetic scroll would otherwise corrupt the previous page's
    // saved position.
    const snapFromScroll = () => {
      if (Date.now() - lastNavAtRef.current < NAV_QUIET_MS) return;
      snap();
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(snapFromScroll);
    };
    // Pointerdown / keydown fire synchronously *before* the navigation a
    // click triggers, so they always capture the outgoing page's real
    // scrollY. No nav-quiet gate needed.
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("pointerdown", snap, { capture: true });
    document.addEventListener("keydown", snap, { capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("pointerdown", snap, { capture: true });
      document.removeEventListener("keydown", snap, { capture: true });
      cancelAnimationFrame(frame);
    };
  }, []);

  // React to navigation.
  useEffect(() => {
    if (navigationType === "POP") {
      const positions = readPositions();
      const targetY = positions[key];
      if (typeof targetY !== "number") {
        window.scrollTo(0, 0);
        return;
      }

      // ResizeObserver-driven restoration. Watch document height; whenever
      // it grows, re-attempt the scroll. Stop when:
      //   - we've reached targetY (scrollY landed within 1 px of it), OR
      //   - the user starts scrolling on their own (don't fight them), OR
      //   - the fallback timeout fires.
      //
      // The original setTimeout-only retry (30 × 50 ms = 1.5 s) was too
      // short for the fleet detail page on a slow network: the data
      // hadn't loaded by the time retries exhausted, so the page was
      // still skeleton-tall and the scroll clamped to the bottom of the
      // partial content — which happened to be roughly where the footer
      // sits, reproducing the original "lands at footer" symptom under a
      // different cause (ONENOW-469 reopen).
      let cancelled = false;
      let observer: ResizeObserver | null = null;
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const stop = () => {
        if (cancelled) return;
        cancelled = true;
        if (observer) observer.disconnect();
        if (intervalId !== null) clearInterval(intervalId);
        if (timeoutId !== null) clearTimeout(timeoutId);
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchstart", onUserScroll);
        window.removeEventListener("keydown", onUserKey);
      };

      const onUserScroll = () => stop();
      const onUserKey = (e: KeyboardEvent) => {
        // Only treat actual scroll-trigger keys as "user wants to take
        // over", not modifiers / typing.
        const scrollKeys = [
          "PageDown",
          "PageUp",
          "End",
          "Home",
          "ArrowDown",
          "ArrowUp",
          " ",
        ];
        if (scrollKeys.includes(e.key)) stop();
      };

      const tryRestore = () => {
        if (cancelled) return;
        const maxY = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        // If the page hasn't grown enough yet to reach the saved position,
        // park the user at the TOP rather than clamping to maxY. Clamping
        // to maxY lands them at the footer of whatever skeleton content
        // happens to be rendered (e.g., a spinner-only early-return on
        // the fleet detail page while `selectedCar` rehydrates) — that's
        // the exact symptom Faiza reproduced on the ONENOW-469 second
        // reopen. We'll re-attempt as content grows; worst case the user
        // sees the top of the page briefly before jumping to position.
        if (maxY < targetY) {
          window.scrollTo(0, 0);
          return;
        }
        // Successful restore. We DON'T `stop()` here on purpose
        // (ONENOW-469 third reopen): in the second-iteration fix we stopped
        // as soon as the first restore succeeded, but the fleet detail
        // page often grows in stages — first paint with cached data lands
        // tall enough to honour the saved Y, then a subsequent re-render
        // (the spinner early-return for a brief moment, a banner image
        // lazy-loading, an async effect that swaps content) shrinks the
        // document, and the browser clamps scrollY downward to maxY of
        // the now-shorter page. By the time the page grows back the user
        // is stuck near the new (small) maxY — which is what Faiza kept
        // seeing as "lands at footer."
        //
        // Instead we keep the interval + ResizeObserver alive and just
        // re-assert `scrollTo(0, targetY)` on every tick. If the page
        // shrinks below targetY we drop to 0 (top, not footer); when it
        // grows back we re-restore. Termination still comes from one of:
        //   - the 10 s hard timeout,
        //   - the user actually scrolling on their own (wheel / touch /
        //     PageDown / Home / ArrowDown / Space — see onUserScroll +
        //     onUserKey), at which point we hand control back.
        // Page-internal scrollIntoView etc. don't trigger those user
        // events, so we'll still snap back over them — that's intentional
        // here because none of those happen on the fleet-detail return
        // path; if a real conflict surfaces later, gate the re-assert
        // behind `Math.abs(scrollY - targetY) > N` so we only correct
        // genuine displacements.
        window.scrollTo(0, targetY);
      };

      // First attempt right after commit.
      tryRestore();

      if (!cancelled) {
        // Re-attempt every time the document grows. The element itself
        // (`<html>`) doesn't change its own height directly — its content
        // does — so observe `<body>` whose `scrollHeight` mirrors the
        // document's effective scroll size.
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(tryRestore);
          observer.observe(document.body);
        }
        // Backstop poll in case ResizeObserver doesn't catch a growth
        // (e.g., absolutely-positioned content with `bottom: …` that
        // expands the scroll area without resizing `<body>`).
        intervalId = setInterval(tryRestore, RESTORE_TICK_MS);
        timeoutId = setTimeout(stop, RESTORE_TIMEOUT_MS);
        window.addEventListener("wheel", onUserScroll, { passive: true });
        window.addEventListener("touchstart", onUserScroll, { passive: true });
        window.addEventListener("keydown", onUserKey);
      }

      return stop;
    }

    if (navigationType === "REPLACE") {
      return;
    }

    // PUSH
    window.scrollTo(0, 0);
  }, [key, navigationType]);

  return null;
};

export default ScrollToTop;
